import { formatLatencyMs } from "@app/date";
import {
  allowedLlmGatewayModels,
  fetchLlmGatewayConfiguration,
  fetchLlmGatewayModels,
  selectLlmGatewayConfiguration,
} from "@app/deploy";
import {
  type FetchLlmGatewayRequestsProps,
  type LlmGatewayRequestsSortBy,
  fetchLlmGatewayRequests,
  selectLlmGatewayRequestPage,
} from "@app/llm-gateway";
import { formatPreciseCurrency } from "@app/number-utils";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useLoader, useQuery, useSelector } from "@app/react";
import { llmGatewayKeyRequestDetailUrl } from "@app/routes";
import { titleCase } from "@app/string-utils";
import { tunaEvent, useTunaHoverEvent } from "@app/tuna";
import type {
  DeployLlmGatewayModel,
  LlmGatewayRequest,
  LlmGatewayRequestStatus,
} from "@app/types";
import { type ZonedDateTime, now } from "@internationalized/date";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  CopyText,
  Group,
  IconChevronRight,
  InputDateTimeRange,
  Label,
  SearchableSelect,
  Select,
  Tooltip,
  tokens,
} from "../shared";
import {
  ControlledDataTable,
  DataTableCell,
  type DataTableCellRendererProps,
  type DataTableColDef,
  type DataTableSort,
  DateTimeCell,
  PillCell,
} from "../shared/datatable";

const PAGE_SIZE = 20;

const SORT_FIELDS: Record<string, LlmGatewayRequestsSortBy> = {
  timestamp: "timestamp",
  latencyMs: "latency",
  totalTokens: "tokens",
  cost: "cost",
};

interface Filters {
  start: ZonedDateTime;
  end: ZonedDateTime;
  status: LlmGatewayRequestStatus | "";
  model: string;
}

const defaultFilters = (): Filters => {
  const end = now("UTC");
  return { start: end.subtract({ days: 7 }), end, status: "", model: "" };
};

const NoValue = () => <span className="text-gray-500">—</span>;

const TokensCell = ({
  data,
}: DataTableCellRendererProps<LlmGatewayRequest>) => {
  const hoverProps = useTunaHoverEvent(
    "llm-gateway-key-hover-tooltip",
    JSON.stringify({
      llmKeyId: data.llmKeyId,
      field: "tokens",
      requestId: data.id,
    }),
  );

  if (data.totalTokens === null) {
    return <NoValue />;
  }

  return (
    <Tooltip
      fluid
      text={
        <div className="grid grid-cols-[6ch_1fr] gap-2">
          <div className="font-semibold">Input</div>
          <div className="text-right">
            {(data.inputTokens || 0).toLocaleString()}
          </div>
          <div className="font-semibold">Output</div>
          <div className="text-right">
            {(data.outputTokens || 0).toLocaleString()}
          </div>
        </div>
      }
    >
      <span {...hoverProps} className="border-b border-dotted border-gray-400">
        {data.totalTokens.toLocaleString()}
      </span>
    </Tooltip>
  );
};

const RequestDetailLinkCell = ({
  data,
}: DataTableCellRendererProps<LlmGatewayRequest>) => (
  <Link
    to={llmGatewayKeyRequestDetailUrl(data.llmKeyId, data.id)}
    aria-label="View request details"
    className="flex justify-center items-center p-1 hover:bg-gray-200 rounded w-8 h-8"
    onClick={() =>
      tunaEvent(
        "llm-gateway-key-request-detail-clicked",
        JSON.stringify({ llmKeyId: data.llmKeyId, requestId: data.id }),
      )
    }
  >
    <IconChevronRight className="w-5 h-5 stroke-black hover:stroke-indigo" />
  </Link>
);

const colDefs: DataTableColDef<LlmGatewayRequest>[] = [
  {
    field: "id",
    headerName: "Request ID",
    sortable: false,
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayRequest>) => (
      <CopyText text={data.id}>
        <span className={`${tokens.type.darker} font-mono`}>{data.id}</span>
      </CopyText>
    ),
  },
  {
    field: "model",
    headerName: "Model",
    sortable: false,
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayRequest>) =>
      data.model === null ? (
        <NoValue />
      ) : (
        <span className={`${tokens.type.darker} font-mono`}>{data.model}</span>
      ),
  },
  {
    field: "operation",
    headerName: "Operation",
    sortable: false,
    cellRenderer: ({ value }: DataTableCellRendererProps<LlmGatewayRequest>) =>
      value === null ? <NoValue /> : titleCase(value),
  },
  {
    field: "status",
    headerName: "Status",
    sortable: false,
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayRequest>) => (
      <PillCell
        value={data.status}
        label={titleCase(data.status)}
        valueToVariantMap={{ success: "success", error: "error" }}
      />
    ),
  },
  {
    field: "timestamp",
    headerName: "Timestamp (UTC)",
    initialSort: "desc",
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayRequest>) => (
      <DateTimeCell date={data.timestamp} />
    ),
  },
  {
    field: "latencyMs",
    headerName: "Latency",
    valueFormatter: ({ value }) => formatLatencyMs(value),
  },
  {
    field: "totalTokens",
    headerName: "Tokens",
    cellRenderer: TokensCell,
  },
  {
    field: "cost",
    headerName: "Cost",
    cellRenderer: ({ data }: DataTableCellRendererProps<LlmGatewayRequest>) =>
      data.cost === null ? (
        <NoValue />
      ) : (
        <DataTableCell label={formatPreciseCurrency(data.cost)} />
      ),
  },
  {
    colId: "actions",
    sortable: false,
    width: 60,
    resizable: false,
    suppressAutoSize: true,
    suppressSizeToFit: true,
    cellRenderer: RequestDetailLinkCell,
  },
];

const FilterBar = ({
  initialFilters,
  models,
  onApply,
}: {
  initialFilters: Filters;
  models: DeployLlmGatewayModel[];
  onApply: (filters: Filters) => void;
}) => {
  const [draft, setDraft] = useState(initialFilters);

  const modelOptions = useMemo(
    () => [
      { value: "", label: "All Models" },
      ...models.map((model) => ({ value: model.id, label: model.id })),
    ],
    [models],
  );

  const reset = () => {
    setDraft(initialFilters);
    onApply(initialFilters);
  };

  return (
    <div className="flex items-end gap-2 max-w-full">
      <div className="flex-shrink-0">
        <Label htmlFor="date-range-requests">Date & Time Range</Label>
        <InputDateTimeRange
          id="date-range-requests"
          value={{ start: draft.start, end: draft.end }}
          onChange={(range) => {
            if (range?.start && range?.end) {
              setDraft({ ...draft, start: range.start, end: range.end });
            }
          }}
          disableFuture
          useUTC
          maxRangeDays={90}
          quickRanges={[
            { label: "Last hour", duration: "1h" },
            { label: "Last day", duration: "1d" },
            { label: "Last 7 days", duration: "7d" },
            { label: "Last 30 days", duration: "30d" },
            { label: "Last 90 days", duration: "90d" },
          ]}
          className="w-full"
        />
      </div>
      <div className="flex-grow">
        <Label htmlFor="status-filter">Status</Label>
        <Select
          id="status-filter"
          value={draft.status}
          options={[
            { label: "All", value: "" },
            { label: "Success", value: "success" },
            { label: "Error", value: "error" },
          ]}
          onSelect={(option) =>
            setDraft({
              ...draft,
              status: option.value as LlmGatewayRequestStatus | "",
            })
          }
          className="w-full"
        />
      </div>
      <div className="flex-grow">
        <Label htmlFor="model-filter">Model</Label>
        <SearchableSelect
          id="model-filter"
          value={draft.model}
          onChange={(value) => setDraft({ ...draft, model: value || "" })}
          options={modelOptions}
          placeholder="All Models"
          className="w-full"
          searchPlaceholder="Search models..."
        />
      </div>
      <div className="flex items-end gap-2 flex-shrink-0">
        <Button type="button" variant="white" size="md" onClick={reset}>
          Reset
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={() => onApply(draft)}
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
};

export const LlmGatewayKeyRequestsPage = () => {
  const { id = "" } = useParams();
  const orgId = useSelector(selectOrganizationSelectedId);

  useQuery(fetchLlmGatewayConfiguration({ orgId }));
  useQuery(fetchLlmGatewayModels());
  const config = useSelector(selectLlmGatewayConfiguration);
  const catalog: DeployLlmGatewayModel[] =
    useLoader(fetchLlmGatewayModels).meta.models || [];
  const models = allowedLlmGatewayModels(config ? config.models : [], catalog);

  // Fixed at page load, so the window doesn't move while paging through it.
  const [initialFilters] = useState(defaultFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState<{
    sortBy: LlmGatewayRequestsSortBy;
    order: FetchLlmGatewayRequestsProps["order"];
  }>({ sortBy: "timestamp", order: "desc" });
  const [page, setPage] = useState(1);

  const action = fetchLlmGatewayRequests({
    orgId,
    llmKeyId: id,
    startTime: filters.start.toAbsoluteString(),
    endTime: filters.end.toAbsoluteString(),
    status: filters.status,
    model: filters.model,
    ...sort,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  useQuery(action);
  const loader = useLoader(action);
  const result = useSelector((s) =>
    selectLlmGatewayRequestPage(s, { key: action.payload.key }),
  );

  // Hold the last total so pagination doesn't flash to zero between pages.
  const [total, setTotal] = useState(0);
  useEffect(() => {
    if (result) setTotal(result.total);
  }, [result]);

  const onApply = (next: Filters) => {
    tunaEvent(
      "llm-gateway-key-filters-changed",
      JSON.stringify({
        llmKeyId: id,
        startTime: next.start.toAbsoluteString(),
        endTime: next.end.toAbsoluteString(),
        status: next.status,
        model: next.model,
      }),
    );
    setFilters(next);
    setPage(1);
  };

  const onSort = (next: DataTableSort | null) => {
    if (!next) return;
    tunaEvent(
      "llm-gateway-key-sort-changed",
      JSON.stringify({
        llmKeyId: id,
        field: next.field,
        direction: next.direction,
      }),
    );
    setSort({
      sortBy: SORT_FIELDS[next.field] || "timestamp",
      order: next.direction,
    });
    setPage(1);
  };

  return (
    <Group>
      <FilterBar
        initialFilters={initialFilters}
        models={models}
        onApply={onApply}
      />

      <hr />

      <ControlledDataTable<LlmGatewayRequest>
        rowData={result ? result.requests : []}
        colDefs={colDefs}
        isLoading={loader.isLoading}
        searchable={false}
        page={page}
        totalPages={Math.max(1, Math.ceil(total / PAGE_SIZE))}
        totalItems={total}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
        onSort={onSort}
      />
    </Group>
  );
};
